import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Stack, TextField, MenuItem, Button, Alert, Chip, FormControlLabel, Switch, Divider } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { emailSettingsApi, type UpdateEmailSettingsInput } from '../../api/emailSettings.api';
import { extractApiError } from '../../api/error';

const PROVIDERS = [
  { value: 'console', label: 'Console (log only)' },
  { value: 'smtp', label: 'SMTP' },
  { value: 'ses', label: 'AWS SES (future)' },
  { value: 'resend', label: 'Resend (future)' },
  { value: 'mailgun', label: 'Mailgun (future)' },
  { value: 'sendgrid', label: 'SendGrid (future)' },
];

export function EmailSettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['email-settings'], queryFn: () => emailSettingsApi.get() });
  const [form, setForm] = useState<UpdateEmailSettingsInput>({});
  const [smtpPassword, setSmtpPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (data) setForm({ provider: data.provider, fromAddress: data.fromAddress, fromName: data.fromName, replyTo: data.replyTo, smtpHost: data.smtpHost, smtpPort: data.smtpPort, smtpSecure: data.smtpSecure, smtpUser: data.smtpUser, active: data.active });
  }, [data]);

  const set = <K extends keyof UpdateEmailSettingsInput>(k: K, v: UpdateEmailSettingsInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const saveMut = useMutation({
    mutationFn: () => emailSettingsApi.update({ ...form, ...(smtpPassword ? { smtpPassword } : {}) }),
    onSuccess: () => { setMsg({ kind: 'success', text: 'Email settings saved.' }); setSmtpPassword(''); qc.invalidateQueries({ queryKey: ['email-settings'] }); },
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });
  const testMut = useMutation({
    mutationFn: () => emailSettingsApi.test(testTo),
    onSuccess: (r) => setMsg({ kind: 'success', text: `Test email sent via ${r.provider} (${r.status}).` }),
    onError: (e) => setMsg({ kind: 'error', text: extractApiError(e) }),
  });

  const isSmtp = form.provider === 'smtp';

  return (
    <>
      <PageHeader title="Email Configuration" subtitle="Provider, sender identity and SMTP credentials for outbound email." />
      <Card sx={{ maxWidth: 720 }}>
        <CardHeader
          title="Provider settings"
          action={data && <Chip size="small" label={`source: ${data.source}`} color={data.source === 'database' ? 'primary' : 'default'} />}
        />
        <CardContent>
          <Stack spacing={2}>
            {msg && <Alert severity={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Alert>}
            <TextField select label="Provider" value={form.provider ?? 'console'} onChange={(e) => set('provider', e.target.value)} fullWidth>
              {PROVIDERS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField label="From address" value={form.fromAddress ?? ''} onChange={(e) => set('fromAddress', e.target.value)} fullWidth />
              <TextField label="From name" value={form.fromName ?? ''} onChange={(e) => set('fromName', e.target.value)} fullWidth />
            </Stack>
            <TextField label="Reply-To" value={form.replyTo ?? ''} onChange={(e) => set('replyTo', e.target.value)} fullWidth />

            {isSmtp && (
              <>
                <Divider textAlign="left">SMTP</Divider>
                <Stack direction="row" spacing={2}>
                  <TextField label="SMTP host" value={form.smtpHost ?? ''} onChange={(e) => set('smtpHost', e.target.value)} fullWidth />
                  <TextField label="Port" type="number" value={form.smtpPort ?? 587} onChange={(e) => set('smtpPort', Number(e.target.value))} sx={{ width: 120 }} />
                  <FormControlLabel control={<Switch checked={form.smtpSecure ?? false} onChange={(e) => set('smtpSecure', e.target.checked)} />} label="TLS" />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField label="SMTP user" value={form.smtpUser ?? ''} onChange={(e) => set('smtpUser', e.target.value)} fullWidth />
                  <TextField label="SMTP password" type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder={data?.hasSmtpPassword ? '•••••• (set — leave blank to keep)' : ''} fullWidth />
                </Stack>
              </>
            )}

            <FormControlLabel control={<Switch checked={form.active ?? true} onChange={(e) => set('active', e.target.checked)} />} label="Active" />
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>Save</Button>
            </Stack>

            <Divider textAlign="left">Send test email</Divider>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label="To address" value={testTo} onChange={(e) => setTestTo(e.target.value)} sx={{ flex: 1 }} />
              <Button variant="outlined" onClick={() => testMut.mutate()} disabled={!testTo || testMut.isPending}>Send Test</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
